import { Badge } from '@viastud/ui/badge'
import { Button } from '@viastud/ui/button'
import { Card, CardContent } from '@viastud/ui/card'
import { SelectChapter } from '@viastud/ui/shared/select-chapter'
import { GradeEnum } from '@viastud/utils'
import type * as React from 'react'

export interface BookingChapter {
  id: number
  name: string
  imageUrl: string | null
  grade: string
  subject: string
  duration?: string
  order: number
  modules: {
    id: number
    name: string
    chapterNumber: string
  }[]
}

export function getBookingChapterImageSrc(chapter: BookingChapter) {
  const imageUrl = chapter.imageUrl?.trim()
  if (!imageUrl) return null
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl
  if (imageUrl === 'test_session.png' || imageUrl.includes('/')) {
    return `/photos/${imageUrl}`
  }

  return `/photos/${chapter.grade.toLowerCase()}/${chapter.order}.png`
}

interface BookingCardProps {
  chapter: BookingChapter
  selectedModuleId?: number
  onModuleChange: (moduleId: number) => void
  disabled?: boolean
  onBook: (chapterId: number, moduleId: number) => void
}

export const BookingCard: React.FC<BookingCardProps> = ({
  chapter,
  selectedModuleId,
  onModuleChange,
  onBook,
  disabled,
}) => {
  const currentModuleId = selectedModuleId ?? chapter.modules?.at(0)?.id
  const chapterNumber = chapter.modules.at(0)?.chapterNumber ?? 1
  const imageSrc = getBookingChapterImageSrc(chapter)

  const isTestChapter = chapter.name.toLowerCase().includes("cours d'essai")
  return (
    <Card className="flex h-full flex-col shadow-none">
      <div
        className="relative aspect-[1209/719] w-full rounded-2xl border-b border-gray-200"
        style={{ backgroundColor: '#FBF9FD' }}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={chapter.name}
            className="h-full w-full rounded-2xl object-contain"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="text-neutral-400">Pas d&#39;image</span>
          </div>
        )}
      </div>
      <CardContent className="flex flex-1 flex-col gap-4 p-4">
        <div className="flex flex-wrap gap-2">
          <Badge
            variant="secondary"
            className="bg-yellow-100 uppercase text-yellow-600 hover:bg-yellow-100"
          >
            {chapterNumber}
          </Badge>
          <Badge variant="secondary" className="bg-blue-100 text-blue-600 hover:bg-blue-100">
            {GradeEnum[chapter.grade as keyof typeof GradeEnum]}
          </Badge>
          {chapter.duration && (
            <Badge variant="secondary" className="bg-gray-50 text-gray-400 hover:bg-gray-50">
              {isTestChapter ? '30 min' : chapter.duration}
            </Badge>
          )}
        </div>

        <h3 className="line-clamp-2 text-lg font-bold leading-tight text-neutral-900">
          {chapter.name.split(':')?.at(1)?.trim() ?? chapter.name}
        </h3>

        <div className="mt-auto flex flex-col gap-4">
          <SelectChapter
            chapter={chapter}
            value={currentModuleId?.toString()}
            onValueChange={(val) => {
              onModuleChange(Number.parseInt(val))
            }}
            disabled={chapter.modules.length <= 1}
          />
          <Button
            className="w-full"
            disabled={isTestChapter ? false : disabled}
            onClick={() => {
              if (currentModuleId) {
                onBook(chapter.id, currentModuleId)
              }
            }}
          >
            Réserver ce cours
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
